import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, MapPin, Calendar, Clock, Users, AlignLeft, Mountain, Dumbbell,
  Car, Landmark, Wine, UtensilsCrossed, Laptop, Languages, Sparkles,
  ChevronRight, ChevronLeft, PartyPopper, Compass, Zap, Check, Settings,
  Gauge, Wallet, Globe, ClipboardList
} from 'lucide-react';
import { ActivityCategory, ActivityDifficulty, ActivityBudget, ACTIVITY_CATEGORIES } from '../../types/activities';
import { budgetCountries } from '../../data/budget-countries';
import { CityAutocomplete } from './CityAutocomplete';

interface CreateActivityFormProps {
  onSubmit: (data: {
    title: string;
    description: string;
    category: ActivityCategory;
    date: string;
    time: string;
    city: string;
    country: string;
    countryCode: string;
    meetingPoint: string;
    maxParticipants: number;
    difficulty: ActivityDifficulty;
    budget: ActivityBudget;
    spokenLanguages: string[];
    requirements: string;
  }) => void;
  onClose: () => void;
  isSubmitting?: boolean;
}

const CATEGORY_ICONS: Record<string, React.ElementType> = {
  Mountain, Dumbbell, Car, MapPin, Landmark, Wine, UtensilsCrossed,
  Laptop, Languages, Sparkles,
};

const CATEGORY_COLORS: Record<ActivityCategory, { bg: string; selected: string; text: string; ring: string }> = {
  'hiking': { bg: 'bg-emerald-50 dark:bg-emerald-900/20', selected: 'bg-emerald-500', text: 'text-emerald-600 dark:text-emerald-400', ring: 'ring-emerald-500/30' },
  'sport': { bg: 'bg-orange-50 dark:bg-orange-900/20', selected: 'bg-orange-500', text: 'text-orange-600 dark:text-orange-400', ring: 'ring-orange-500/30' },
  'roadtrip': { bg: 'bg-sky-50 dark:bg-sky-900/20', selected: 'bg-sky-500', text: 'text-sky-600 dark:text-sky-400', ring: 'ring-sky-500/30' },
  'city-tour': { bg: 'bg-teal-50 dark:bg-teal-900/20', selected: 'bg-teal-500', text: 'text-teal-600 dark:text-teal-400', ring: 'ring-teal-500/30' },
  'cultural': { bg: 'bg-amber-50 dark:bg-amber-900/20', selected: 'bg-amber-500', text: 'text-amber-600 dark:text-amber-400', ring: 'ring-amber-500/30' },
  'nightlife': { bg: 'bg-rose-50 dark:bg-rose-900/20', selected: 'bg-rose-500', text: 'text-rose-600 dark:text-rose-400', ring: 'ring-rose-500/30' },
  'food': { bg: 'bg-red-50 dark:bg-red-900/20', selected: 'bg-red-500', text: 'text-red-600 dark:text-red-400', ring: 'ring-red-500/30' },
  'coworking': { bg: 'bg-blue-50 dark:bg-blue-900/20', selected: 'bg-blue-500', text: 'text-blue-600 dark:text-blue-400', ring: 'ring-blue-500/30' },
  'language-exchange': { bg: 'bg-cyan-50 dark:bg-cyan-900/20', selected: 'bg-cyan-500', text: 'text-cyan-600 dark:text-cyan-400', ring: 'ring-cyan-500/30' },
  'other': { bg: 'bg-gray-50 dark:bg-gray-700/30', selected: 'bg-gray-500', text: 'text-gray-600 dark:text-gray-400', ring: 'ring-gray-500/30' },
};

const TOTAL_STEPS = 5;

const STEP_TITLES = [
  { title: 'Quel type ?', subtitle: 'Choisis la vibe de ton activité', icon: Compass },
  { title: 'Raconte-nous', subtitle: 'Donne envie aux autres de te rejoindre', icon: Zap },
  { title: 'Où et quand ?', subtitle: 'Les détails pratiques', icon: MapPin },
  { title: 'Les options', subtitle: 'Places, niveau, budget & langues', icon: Settings },
  { title: 'Derniers détails', subtitle: 'Presque fini !', icon: PartyPopper },
];

const TITLE_PLACEHOLDERS: Partial<Record<ActivityCategory, string>> = {
  'hiking': 'Ex: Rando au coucher du soleil à Blue Mountains',
  'sport': 'Ex: Session surf à Bondi Beach',
  'roadtrip': 'Ex: Road trip sur la Great Ocean Road',
  'city-tour': 'Ex: Découverte des street arts de Melbourne',
  'cultural': 'Ex: Visite du musée des beaux-arts',
  'nightlife': 'Ex: Bar crawl dans le quartier latin',
  'food': 'Ex: Dégustation de ramen à Shibuya',
  'coworking': 'Ex: Session coworking au café du coin',
  'language-exchange': "Ex: Échange français-anglais autour d'un café",
  'other': 'Ex: Soirée jeux de société',
};

const DIFFICULTY_OPTIONS: { id: ActivityDifficulty; label: string; emoji: string; desc: string }[] = [
  { id: 'easy', label: 'Facile', emoji: '🟢', desc: 'Accessible à tous' },
  { id: 'moderate', label: 'Modéré', emoji: '🟡', desc: "Un peu d'effort" },
  { id: 'challenging', label: 'Sportif', emoji: '🔴', desc: 'Bonne condition requise' },
];

const BUDGET_OPTIONS: { id: ActivityBudget; label: string; icon: string; desc: string }[] = [
  { id: 'free', label: 'Gratuit', icon: '🆓', desc: '0 €' },
  { id: 'cheap', label: 'Pas cher', icon: '💰', desc: '< 20 €' },
  { id: 'moderate', label: 'Moyen', icon: '💰💰', desc: '20-50 €' },
  { id: 'expensive', label: 'Cher', icon: '💰💰💰', desc: '> 50 €' },
];

const LANGUAGE_OPTIONS = [
  { code: 'fr', label: 'Français', flag: '🇫🇷' },
  { code: 'en', label: 'Anglais', flag: '🇬🇧' },
  { code: 'es', label: 'Espagnol', flag: '🇪🇸' },
  { code: 'pt', label: 'Portugais', flag: '🇧🇷' },
  { code: 'de', label: 'Allemand', flag: '🇩🇪' },
  { code: 'it', label: 'Italien', flag: '🇮🇹' },
  { code: 'ja', label: 'Japonais', flag: '🇯🇵' },
  { code: 'ko', label: 'Coréen', flag: '🇰🇷' },
  { code: 'zh', label: 'Chinois', flag: '🇨🇳' },
  { code: 'other', label: 'Autre', flag: '🌍' },
];

interface CategoryConfig {
  showDifficulty: boolean;
  defaultBudget: ActivityBudget;
  defaultDifficulty: ActivityDifficulty;
  defaultMaxParticipants: number;
  requirementsPlaceholder: string;
  budgetLabel: string;
  participantsLabel: string;
}

const CATEGORY_CONFIG: Record<ActivityCategory, CategoryConfig> = {
  'hiking': {
    showDifficulty: true,
    defaultBudget: 'free',
    defaultDifficulty: 'moderate',
    defaultMaxParticipants: 8,
    requirementsPlaceholder: 'Ex: Chaussures de rando, crème solaire, 1L d\'eau minimum...',
    budgetLabel: 'Budget estimé (transport, entrées...)',
    participantsLabel: 'Taille du groupe',
  },
  'sport': {
    showDifficulty: true,
    defaultBudget: 'cheap',
    defaultDifficulty: 'moderate',
    defaultMaxParticipants: 10,
    requirementsPlaceholder: 'Ex: Tenue de sport, serviette, niveau intermédiaire...',
    budgetLabel: 'Budget estimé (location, entrée...)',
    participantsLabel: 'Nombre de joueurs max',
  },
  'roadtrip': {
    showDifficulty: false,
    defaultBudget: 'moderate',
    defaultDifficulty: 'easy',
    defaultMaxParticipants: 4,
    requirementsPlaceholder: 'Ex: Permis de conduire, participation essence, sac de couchage...',
    budgetLabel: 'Budget estimé par personne',
    participantsLabel: 'Nombre de places en voiture',
  },
  'city-tour': {
    showDifficulty: false,
    defaultBudget: 'free',
    defaultDifficulty: 'easy',
    defaultMaxParticipants: 10,
    requirementsPlaceholder: 'Ex: Chaussures confortables, appareil photo...',
    budgetLabel: 'Budget estimé (transports, entrées...)',
    participantsLabel: 'Taille du groupe',
  },
  'cultural': {
    showDifficulty: false,
    defaultBudget: 'cheap',
    defaultDifficulty: 'easy',
    defaultMaxParticipants: 8,
    requirementsPlaceholder: 'Ex: Réservation nécessaire, tenue correcte...',
    budgetLabel: 'Budget estimé (entrées, billets...)',
    participantsLabel: 'Nombre de participants max',
  },
  'nightlife': {
    showDifficulty: false,
    defaultBudget: 'moderate',
    defaultDifficulty: 'easy',
    defaultMaxParticipants: 15,
    requirementsPlaceholder: 'Ex: Pièce d\'identité, dress code...',
    budgetLabel: 'Budget estimé (consos, entrées...)',
    participantsLabel: 'Nombre de fêtards max',
  },
  'food': {
    showDifficulty: false,
    defaultBudget: 'moderate',
    defaultDifficulty: 'easy',
    defaultMaxParticipants: 6,
    requirementsPlaceholder: 'Ex: Allergies à signaler, réservation faite...',
    budgetLabel: 'Budget estimé par personne',
    participantsLabel: 'Nombre de gourmands max',
  },
  'coworking': {
    showDifficulty: false,
    defaultBudget: 'cheap',
    defaultDifficulty: 'easy',
    defaultMaxParticipants: 8,
    requirementsPlaceholder: 'Ex: Ordinateur portable, casque audio...',
    budgetLabel: 'Budget estimé (café, espace...)',
    participantsLabel: 'Nombre de places',
  },
  'language-exchange': {
    showDifficulty: false,
    defaultBudget: 'free',
    defaultDifficulty: 'easy',
    defaultMaxParticipants: 10,
    requirementsPlaceholder: 'Ex: Niveau débutant accepté, apporter un cahier...',
    budgetLabel: 'Budget estimé',
    participantsLabel: 'Nombre de participants max',
  },
  'other': {
    showDifficulty: false,
    defaultBudget: 'free',
    defaultDifficulty: 'easy',
    defaultMaxParticipants: 10,
    requirementsPlaceholder: 'Ex: Ce qu\'il faut prévoir ou apporter...',
    budgetLabel: 'Budget estimé',
    participantsLabel: 'Nombre de participants max',
  },
};

function getAccentColor(category: ActivityCategory): string {
  const map: Record<ActivityCategory, string> = {
    'hiking': '#10b981', 'sport': '#f97316', 'roadtrip': '#0ea5e9',
    'city-tour': '#14b8a6', 'cultural': '#f59e0b', 'nightlife': '#f43f5e',
    'food': '#ef4444', 'coworking': '#3b82f6', 'language-exchange': '#06b6d4',
    'other': '#6b7280',
  };
  return map[category] || '#14b8a6';
}

export function CreateActivityForm({ onSubmit, onClose, isSubmitting }: CreateActivityFormProps) {
  const [step, setStep] = React.useState(0);
  const [direction, setDirection] = React.useState(1);
  const [title, setTitle] = React.useState('');
  const [description, setDescription] = React.useState('');
  const [category, setCategory] = React.useState<ActivityCategory | ''>('');
  const [date, setDate] = React.useState('');
  const [time, setTime] = React.useState('');
  const [city, setCity] = React.useState('');
  const [countryCode, setCountryCode] = React.useState('');
  const [meetingPoint, setMeetingPoint] = React.useState('');
  const [maxParticipants, setMaxParticipants] = React.useState(5);
  const [difficulty, setDifficulty] = React.useState<ActivityDifficulty>('easy');
  const [budget, setBudget] = React.useState<ActivityBudget>('free');
  const [spokenLanguages, setSpokenLanguages] = React.useState<string[]>(['fr']);
  const [requirements, setRequirements] = React.useState('');
  const [hasInitOptions, setHasInitOptions] = React.useState(false);

  const selectedCountry = budgetCountries.find(c => c.code === countryCode);
  const catConfig = category ? CATEGORY_CONFIG[category] : null;

  React.useEffect(() => {
    if (category && !hasInitOptions) {
      const config = CATEGORY_CONFIG[category];
      setMaxParticipants(config.defaultMaxParticipants);
      setDifficulty(config.defaultDifficulty);
      setBudget(config.defaultBudget);
      setHasInitOptions(true);
    }
  }, [category, hasInitOptions]);

  const canProceed = (s: number) => {
    switch (s) {
      case 0: return category !== '';
      case 1: return title.trim().length >= 3 && description.trim().length >= 10;
      case 2: return countryCode !== '' && city.trim().length > 0 && date !== '' && time !== '';
      case 3: return spokenLanguages.length > 0;
      case 4: return true;
      default: return false;
    }
  };

  const goNext = () => {
    if (step < TOTAL_STEPS - 1 && canProceed(step)) {
      setDirection(1);
      setStep(step + 1);
    }
  };

  const goPrev = () => {
    if (step > 0) {
      setDirection(-1);
      setStep(step - 1);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCountry || !category) return;
    onSubmit({
      title,
      description,
      category,
      date,
      time,
      city,
      country: selectedCountry.name,
      countryCode,
      meetingPoint,
      maxParticipants,
      difficulty,
      budget,
      spokenLanguages,
      requirements,
    });
  };

  const today = new Date().toISOString().split('T')[0];

  const slideVariants = {
    enter: (d: number) => ({ x: d > 0 ? 80 : -80, opacity: 0 }),
    center: { x: 0, opacity: 1 },
    exit: (d: number) => ({ x: d < 0 ? 80 : -80, opacity: 0 }),
  };

  const selectedCat = ACTIVITY_CATEGORIES.find(c => c.id === category);
  const catColor = category ? CATEGORY_COLORS[category] : null;
  const isLastStep = step === TOTAL_STEPS - 1;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4 z-50 overflow-hidden">
      <motion.div
        initial={{ y: 60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 60, opacity: 0 }}
        transition={{ type: 'spring', damping: 25, stiffness: 300 }}
        className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[92vh] sm:max-h-[92vh] overflow-hidden flex flex-col max-w-full"
      >
        <div className="w-12 h-1 bg-gray-300 dark:bg-gray-600 rounded-full mx-auto mt-3 sm:hidden" />

        <div className="px-4 sm:px-5 pt-4 pb-3">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <motion.div
                key={step}
                initial={{ scale: 0.8, rotate: -10 }}
                animate={{ scale: 1, rotate: 0 }}
                className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  catColor ? catColor.bg : 'bg-teal-50 dark:bg-teal-900/20'
                }`}
              >
                {React.createElement(STEP_TITLES[step].icon, {
                  className: `w-5 h-5 ${catColor ? catColor.text : 'text-teal-600 dark:text-teal-400'}`,
                })}
              </motion.div>
              <div>
                <h2 className="text-lg font-bold text-gray-900 dark:text-white">
                  {STEP_TITLES[step].title}
                </h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {STEP_TITLES[step].subtitle}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-full transition-colors touch-manipulation"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="flex gap-1.5">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div key={i} className="flex-1 h-1.5 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-700">
                <motion.div
                  className={`h-full rounded-full ${catColor ? catColor.selected : 'bg-teal-500'}`}
                  initial={false}
                  animate={{ width: i < step ? '100%' : i === step ? '50%' : '0%' }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                />
              </div>
            ))}
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto overscroll-contain overflow-x-hidden">
          <div className={`px-4 sm:px-5 py-4 ${step <= 2 ? 'min-h-[420px]' : ''}`}>
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={step}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: 'easeInOut' }}
              >
                {step === 0 && (
                  <StepCategory category={category} setCategory={setCategory} />
                )}
                {step === 1 && (
                  <StepDetails
                    title={title} setTitle={setTitle}
                    description={description} setDescription={setDescription}
                    category={category as ActivityCategory} catColor={catColor}
                  />
                )}
                {step === 2 && (
                  <StepLocation
                    countryCode={countryCode} setCountryCode={setCountryCode}
                    city={city} setCity={setCity}
                    date={date} setDate={setDate}
                    time={time} setTime={setTime}
                    today={today} catColor={catColor}
                  />
                )}
                {step === 3 && catConfig && (
                  <StepOptions
                    maxParticipants={maxParticipants} setMaxParticipants={setMaxParticipants}
                    difficulty={difficulty} setDifficulty={setDifficulty}
                    budget={budget} setBudget={setBudget}
                    spokenLanguages={spokenLanguages} setSpokenLanguages={setSpokenLanguages}
                    requirements={requirements} setRequirements={setRequirements}
                    category={category as ActivityCategory} catColor={catColor}
                    catConfig={catConfig}
                  />
                )}
                {step === 4 && (
                  <StepPreview
                    meetingPoint={meetingPoint} setMeetingPoint={setMeetingPoint}
                    catColor={catColor} category={category as ActivityCategory}
                    title={title} city={city} selectedCountry={selectedCountry}
                    date={date} time={time} selectedCat={selectedCat}
                    maxParticipants={maxParticipants} difficulty={difficulty}
                    budget={budget} spokenLanguages={spokenLanguages}
                    showDifficulty={catConfig?.showDifficulty ?? false}
                  />
                )}
              </motion.div>
            </AnimatePresence>
          </div>

          <div className="px-4 sm:px-5 pb-6 pt-2 border-t dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
            <div className="flex gap-3">
              {step > 0 && (
                <motion.button
                  type="button"
                  onClick={goPrev}
                  whileTap={{ scale: 0.97 }}
                  className="flex items-center gap-1.5 px-4 py-3 sm:py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-xl transition-colors touch-manipulation"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Retour
                </motion.button>
              )}
              {step === 0 && (
                <motion.button
                  type="button"
                  onClick={onClose}
                  whileTap={{ scale: 0.97 }}
                  className="px-4 py-3 sm:py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 active:bg-gray-200 rounded-xl transition-colors touch-manipulation"
                >
                  Annuler
                </motion.button>
              )}
              {!isLastStep ? (
                <motion.button
                  type="button"
                  onClick={goNext}
                  disabled={!canProceed(step)}
                  whileTap={{ scale: 0.97 }}
                  className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 sm:py-2.5 text-sm font-semibold text-white rounded-xl transition-all touch-manipulation disabled:opacity-40 disabled:cursor-not-allowed shadow-sm ${
                    catColor ? `${catColor.selected} hover:opacity-90` : 'bg-teal-600 hover:bg-teal-700'
                  }`}
                >
                  Continuer
                  <ChevronRight className="w-4 h-4" />
                </motion.button>
              ) : (
                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileTap={{ scale: 0.97 }}
                  className={`flex-1 flex items-center justify-center gap-2 px-5 py-3 sm:py-2.5 text-sm font-semibold text-white rounded-xl transition-all touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed shadow-sm ${
                    catColor ? `${catColor.selected} hover:opacity-90` : 'bg-teal-600 hover:bg-teal-700'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ repeat: Infinity, duration: 0.8, ease: 'linear' }}
                        className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                      />
                      Publication...
                    </>
                  ) : (
                    <>
                      <PartyPopper className="w-4 h-4" />
                      C'est parti, je publie !
                    </>
                  )}
                </motion.button>
              )}
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function StepCategory({
  category, setCategory,
}: {
  category: ActivityCategory | '';
  setCategory: (c: ActivityCategory) => void;
}) {
  return (
    <div className="grid grid-cols-2 gap-2.5">
      {ACTIVITY_CATEGORIES.map((cat, i) => {
        const Icon = CATEGORY_ICONS[cat.icon] || Sparkles;
        const colors = CATEGORY_COLORS[cat.id];
        const isSelected = category === cat.id;
        return (
          <motion.button
            key={cat.id}
            type="button"
            onClick={() => setCategory(cat.id)}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.04, duration: 0.3 }}
            whileTap={{ scale: 0.96 }}
            className={`relative flex items-center gap-3 p-3.5 rounded-xl text-left transition-all touch-manipulation border-2 ${
              isSelected
                ? `${colors.bg} border-current ${colors.text} ring-2 ${colors.ring}`
                : 'bg-white dark:bg-gray-700/40 border-gray-100 dark:border-gray-700 hover:border-gray-200 dark:hover:border-gray-600'
            }`}
          >
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors ${
              isSelected ? colors.selected : 'bg-gray-100 dark:bg-gray-600/50'
            }`}>
              <Icon className={`w-4.5 h-4.5 ${isSelected ? 'text-white' : 'text-gray-500 dark:text-gray-400'}`} />
            </div>
            <span className={`text-sm font-medium leading-tight ${
              isSelected ? colors.text : 'text-gray-700 dark:text-gray-300'
            }`}>
              {cat.label}
            </span>
            {isSelected && (
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                className={`absolute top-1.5 right-1.5 w-5 h-5 rounded-full flex items-center justify-center ${colors.selected}`}
              >
                <Check className="w-3 h-3 text-white" />
              </motion.div>
            )}
          </motion.button>
        );
      })}
    </div>
  );
}

function StepDetails({
  title, setTitle, description, setDescription, category, catColor,
}: {
  title: string;
  setTitle: (v: string) => void;
  description: string;
  setDescription: (v: string) => void;
  category: ActivityCategory;
  catColor: { bg: string; selected: string; text: string; ring: string } | null;
}) {
  const placeholder = TITLE_PLACEHOLDERS[category] || 'Ex: Mon activité géniale';
  const descLength = description.length;
  const descPercent = Math.min((descLength / 1000) * 100, 100);

  return (
    <div className="space-y-5">
      <div>
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          Titre accrocheur
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={placeholder}
          maxLength={100}
          className={`w-full px-4 py-3.5 sm:py-3 border-2 rounded-xl text-base sm:text-sm dark:bg-gray-700/50 dark:text-white transition-colors focus:outline-none ${
            title.length >= 3
              ? `border-gray-200 dark:border-gray-600 focus:${catColor ? catColor.ring : 'ring-teal-500/20'} focus:border-teal-500`
              : 'border-gray-200 dark:border-gray-600 focus:border-teal-500'
          }`}
          autoFocus
        />
        <div className="flex justify-between mt-1.5">
          <p className="text-xs text-gray-400">
            {title.length < 3 ? `Encore ${3 - title.length} caractère${3 - title.length > 1 ? 's' : ''}` : ''}
          </p>
          <p className="text-xs text-gray-400">{title.length}/100</p>
        </div>
      </div>

      <div>
        <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          <AlignLeft className="w-4 h-4" />
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Décris l'ambiance, le niveau requis, ce qu'il faut apporter... Plus c'est détaillé, plus les gens auront envie de te rejoindre !"
          rows={5}
          maxLength={1000}
          className="w-full px-4 py-3.5 sm:py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-base sm:text-sm dark:bg-gray-700/50 dark:text-white resize-none focus:outline-none focus:border-teal-500 transition-colors"
        />
        <div className="flex items-center gap-3 mt-1.5">
          <div className="flex-1 h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
            <motion.div
              className={`h-full rounded-full ${
                descPercent >= 5 ? (catColor ? catColor.selected : 'bg-teal-500') : 'bg-gray-300'
              }`}
              animate={{ width: `${descPercent}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>
          <span className="text-xs text-gray-400 tabular-nums">{descLength}/1000</span>
        </div>
        {descLength < 10 && descLength > 0 && (
          <p className="text-xs text-amber-500 mt-1">Encore quelques mots pour donner envie...</p>
        )}
      </div>
    </div>
  );
}

function StepLocation({
  countryCode, setCountryCode, city, setCity, date, setDate, time, setTime, today, catColor,
}: {
  countryCode: string;
  setCountryCode: (v: string) => void;
  city: string;
  setCity: (v: string) => void;
  date: string;
  setDate: (v: string) => void;
  time: string;
  setTime: (v: string) => void;
  today: string;
  catColor: { bg: string; selected: string; text: string; ring: string } | null;
}) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <MapPin className="w-4 h-4" />
            Pays
          </label>
          <select
            value={countryCode}
            onChange={(e) => setCountryCode(e.target.value)}
            className="w-full px-4 py-3.5 sm:py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-base sm:text-sm dark:bg-gray-700/50 dark:text-white focus:outline-none focus:border-teal-500 transition-colors"
          >
            <option value="">Choisir un pays</option>
            {budgetCountries.map((c) => (
              <option key={c.code} value={c.code}>{c.flag} {c.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <MapPin className="w-4 h-4" />
            Ville
          </label>
          <CityAutocomplete
            value={city}
            onChange={setCity}
            countryCode={countryCode}
            placeholder="Rechercher une ville..."
          />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <Calendar className="w-4 h-4" />
            Date
          </label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            min={today}
            className="w-full px-3 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm dark:bg-gray-700/50 dark:text-white focus:outline-none focus:border-teal-500 transition-colors"
          />
        </div>
        <div>
          <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
            <Clock className="w-4 h-4" />
            Heure
          </label>
          <input
            type="time"
            value={time}
            onChange={(e) => setTime(e.target.value)}
            className="w-full px-3 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-sm dark:bg-gray-700/50 dark:text-white focus:outline-none focus:border-teal-500 transition-colors"
          />
        </div>
      </div>

      {countryCode && city && date && time && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className={`flex items-center gap-3 p-3 rounded-xl ${catColor ? catColor.bg : 'bg-teal-50 dark:bg-teal-900/20'}`}
        >
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${catColor ? catColor.selected : 'bg-teal-500'}`}>
            <Check className="w-4 h-4 text-white" />
          </div>
          <p className={`text-sm font-medium ${catColor ? catColor.text : 'text-teal-700 dark:text-teal-300'}`}>
            Super ! On se retrouve à {city} le {new Date(date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' })} à {time}
          </p>
        </motion.div>
      )}
    </div>
  );
}

function StepOptions({
  maxParticipants, setMaxParticipants,
  difficulty, setDifficulty,
  budget, setBudget,
  spokenLanguages, setSpokenLanguages,
  requirements, setRequirements,
  category, catColor, catConfig,
}: {
  maxParticipants: number;
  setMaxParticipants: (v: number) => void;
  difficulty: ActivityDifficulty;
  setDifficulty: (v: ActivityDifficulty) => void;
  budget: ActivityBudget;
  setBudget: (v: ActivityBudget) => void;
  spokenLanguages: string[];
  setSpokenLanguages: (v: string[]) => void;
  requirements: string;
  setRequirements: (v: string) => void;
  category: ActivityCategory;
  catColor: { bg: string; selected: string; text: string; ring: string } | null;
  catConfig: CategoryConfig;
}) {
  const toggleLanguage = (code: string) => {
    setSpokenLanguages(
      spokenLanguages.includes(code)
        ? spokenLanguages.filter(l => l !== code)
        : [...spokenLanguages, code]
    );
  };

  const accentHex = getAccentColor(category);

  return (
    <div className="space-y-5">
      <div>
        <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2.5">
          <Users className="w-4 h-4" />
          {catConfig.participantsLabel}
        </label>
        <div className="flex items-center gap-4">
          <div className="flex-1 relative h-8 flex items-center">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-600 rounded-full" />
            </div>
            <div
              className="absolute top-1/2 -translate-y-1/2 left-0 h-2 rounded-full pointer-events-none transition-all duration-200"
              style={{ width: `${((maxParticipants - 2) / (50 - 2)) * 100}%`, backgroundColor: accentHex }}
            />
            <input
              type="range"
              min={2}
              max={50}
              value={maxParticipants}
              onChange={(e) => setMaxParticipants(Number(e.target.value))}
              className="relative w-full h-2 bg-transparent rounded-full appearance-none cursor-pointer z-10
                [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5
                [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:shadow-lg
                [&::-webkit-slider-thumb]:border-4 [&::-webkit-slider-thumb]:cursor-pointer
                [&::-moz-range-thumb]:w-5 [&::-moz-range-thumb]:h-5 [&::-moz-range-thumb]:rounded-full
                [&::-moz-range-thumb]:bg-white [&::-moz-range-thumb]:shadow-lg [&::-moz-range-thumb]:border-4
                [&::-moz-range-thumb]:cursor-pointer [&::-moz-range-thumb]:border-0"
              style={{ borderColor: accentHex } as React.CSSProperties}
            />
          </div>
          <motion.span
            key={maxParticipants}
            initial={{ scale: 1.2 }}
            animate={{ scale: 1 }}
            className={`min-w-[3.5rem] text-center text-sm font-bold px-3 py-2 rounded-xl ${
              catColor ? `${catColor.bg} ${catColor.text}` : 'bg-teal-50 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400'
            }`}
          >
            {maxParticipants}
          </motion.span>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1.5">
          {maxParticipants <= 5 ? 'Petit comité, plus intime' : maxParticipants <= 15 ? 'Un bon groupe, convivial' : maxParticipants <= 30 ? 'Grand groupe, ambiance assurée !' : 'Carrément un événement !'}
        </p>
      </div>

      {catConfig.showDifficulty && (
        <div>
          <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2.5">
            <Gauge className="w-4 h-4" />
            Niveau de difficulté
          </label>
          <div className="grid grid-cols-3 gap-2">
            {DIFFICULTY_OPTIONS.map(opt => (
              <button
                key={opt.id}
                type="button"
                onClick={() => setDifficulty(opt.id)}
                className={`p-3 rounded-xl border-2 text-center transition-all touch-manipulation ${
                  difficulty === opt.id
                    ? `${catColor ? `${catColor.bg} border-current ${catColor.text}` : 'bg-teal-50 border-teal-500 text-teal-600'} ring-2 ${catColor ? catColor.ring : 'ring-teal-500/20'}`
                    : 'bg-white dark:bg-gray-700/40 border-gray-100 dark:border-gray-700 hover:border-gray-200 text-gray-700 dark:text-gray-300'
                }`}
              >
                <span className="text-lg">{opt.emoji}</span>
                <p className="text-xs font-semibold mt-1">{opt.label}</p>
                <p className="text-[10px] text-gray-400 mt-0.5">{opt.desc}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      <div>
        <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2.5">
          <Wallet className="w-4 h-4" />
          {catConfig.budgetLabel}
        </label>
        <div className="grid grid-cols-4 gap-2">
          {BUDGET_OPTIONS.map(opt => (
            <button
              key={opt.id}
              type="button"
              onClick={() => setBudget(opt.id)}
              className={`p-2.5 rounded-xl border-2 text-center transition-all touch-manipulation ${
                budget === opt.id
                  ? `${catColor ? `${catColor.bg} border-current ${catColor.text}` : 'bg-teal-50 border-teal-500 text-teal-600'} ring-2 ${catColor ? catColor.ring : 'ring-teal-500/20'}`
                  : 'bg-white dark:bg-gray-700/40 border-gray-100 dark:border-gray-700 hover:border-gray-200 text-gray-700 dark:text-gray-300'
              }`}
            >
              <span className="text-sm">{opt.icon}</span>
              <p className="text-[11px] font-semibold mt-1">{opt.label}</p>
              <p className="text-[10px] text-gray-400">{opt.desc}</p>
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2.5">
          <Globe className="w-4 h-4" />
          Langues parlées
        </label>
        <div className="flex flex-wrap gap-2">
          {LANGUAGE_OPTIONS.map(lang => (
            <button
              key={lang.code}
              type="button"
              onClick={() => toggleLanguage(lang.code)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-xl border-2 text-sm transition-all touch-manipulation ${
                spokenLanguages.includes(lang.code)
                  ? `${catColor ? `${catColor.bg} border-current ${catColor.text}` : 'bg-teal-50 border-teal-500 text-teal-600'}`
                  : 'bg-white dark:bg-gray-700/40 border-gray-100 dark:border-gray-700 text-gray-600 dark:text-gray-400'
              }`}
            >
              <span>{lang.flag}</span>
              <span className="text-xs font-medium">{lang.label}</span>
              {spokenLanguages.includes(lang.code) && <Check className="w-3 h-3" />}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          <ClipboardList className="w-4 h-4" />
          À prévoir / pré-requis
          <span className="text-xs text-gray-400 font-normal">(optionnel)</span>
        </label>
        <input
          type="text"
          value={requirements}
          onChange={(e) => setRequirements(e.target.value)}
          placeholder={catConfig.requirementsPlaceholder}
          maxLength={200}
          className="w-full px-4 py-3.5 sm:py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-base sm:text-sm dark:bg-gray-700/50 dark:text-white focus:outline-none focus:border-teal-500 transition-colors"
        />
      </div>
    </div>
  );
}

function StepPreview({
  meetingPoint, setMeetingPoint, catColor, category,
  title, city, selectedCountry, date, time, selectedCat,
  maxParticipants, difficulty, budget, spokenLanguages, showDifficulty,
}: {
  meetingPoint: string;
  setMeetingPoint: (v: string) => void;
  catColor: { bg: string; selected: string; text: string; ring: string } | null;
  category: ActivityCategory;
  title: string;
  city: string;
  selectedCountry: { name: string; flag: string } | undefined;
  date: string;
  time: string;
  selectedCat: { label: string } | undefined;
  maxParticipants: number;
  difficulty: ActivityDifficulty;
  budget: ActivityBudget;
  spokenLanguages: string[];
  showDifficulty: boolean;
}) {
  const Icon = CATEGORY_ICONS[ACTIVITY_CATEGORIES.find(c => c.id === category)?.icon || 'Sparkles'] || Sparkles;
  const diffLabel = DIFFICULTY_OPTIONS.find(d => d.id === difficulty);
  const budgetLabel = BUDGET_OPTIONS.find(b => b.id === budget);
  const langLabels = LANGUAGE_OPTIONS.filter(l => spokenLanguages.includes(l.code));

  return (
    <div className="space-y-4">
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        className={`p-3 rounded-xl border-2 border-dashed ${
          catColor ? `${catColor.bg} border-current/20` : 'bg-teal-50 dark:bg-teal-900/20 border-teal-200 dark:border-teal-800'
        }`}
      >
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${catColor ? catColor.selected : 'bg-teal-500'}`}>
            <Icon className="w-4 h-4 text-white" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="font-bold text-gray-900 dark:text-white text-sm truncate">{title}</h3>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
              <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                {city}{selectedCountry ? `, ${selectedCountry.flag}` : ''}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                {date ? new Date(date + 'T00:00:00').toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' }) : ''}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {time}
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 mt-2.5 pt-2.5 border-t border-current/10">
          {selectedCat && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
              catColor ? `${catColor.bg} ${catColor.text}` : 'bg-teal-100 text-teal-700'
            }`}>
              {selectedCat.label}
            </span>
          )}
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
            {maxParticipants} places
          </span>
          {showDifficulty && diffLabel && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {diffLabel.emoji} {diffLabel.label}
            </span>
          )}
          {budgetLabel && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {budgetLabel.label}
            </span>
          )}
          {langLabels.map(l => (
            <span key={l.code} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
              {l.flag}
            </span>
          ))}
        </div>
      </motion.div>

      <div>
        <label className="flex items-center gap-1.5 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          <MapPin className="w-4 h-4" />
          Point de rendez-vous
          <span className="text-xs text-gray-400 font-normal">(optionnel)</span>
        </label>
        <input
          type="text"
          value={meetingPoint}
          onChange={(e) => setMeetingPoint(e.target.value)}
          placeholder="Ex: Devant la station Central, sortie nord"
          className="w-full px-4 py-3.5 sm:py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl text-base sm:text-sm dark:bg-gray-700/50 dark:text-white focus:outline-none focus:border-teal-500 transition-colors"
        />
      </div>
    </div>
  );
}
